import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EditInvoiceModal from "./EditInvoiceModal";
import axios from "axios";
import toast from "react-hot-toast";
import { IoCheckmark } from "react-icons/io5";
import { REST_API_URL } from "../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function InvoiceModal({ invoice, onClose , onInvoiceUpdated}) {
  const [showEdit, setShowEdit] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  if (!invoice) return null;

  const {
    id,
    invoice_number,
    invoice_date,
    payment_due,
    calculated_total_price,
    service_total,
    tax,
    tax_amount,
    discount,
    discount_amount,
    services_details,
    pdf_file_link,
    customer_details,
  } = invoice;

  const customerName = customer_details?.name || "—";
  const customerEmail = customer_details?.email || "—";
  const customerImageUrl =
    customer_details?.image_url || "https://via.placeholder.com/70?text=User";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  };

  const services = Array.isArray(services_details) ? services_details : [];

  const subtotal =
    typeof service_total === "number"
      ? service_total
      : typeof calculated_total_price === "number"
      ? calculated_total_price
      : 0;

  const taxPercent =
    typeof tax === "number" ? tax : parseInt(tax ?? 0, 10) || 0;
  const discountPercent =
    typeof discount === "number" ? discount : parseInt(discount ?? 0, 10) || 0;

  const taxAmt = typeof tax_amount === "number" ? tax_amount : 0;
  const discountAmt = typeof discount_amount === "number" ? discount_amount : 0;

  const total =
    typeof calculated_total_price === "number"
      ? calculated_total_price
      : subtotal + taxAmt - discountAmt;

  const paymentDueText =
    typeof payment_due === "number" ? `Within ${payment_due} days` : "—";

  const handleMarkAsPaid = async () => {
    if (!id) {
      toast.error("Invoice ID not found");
      return;
    }

    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("accessToken") ||
          sessionStorage.getItem("accessToken")
        : null;

    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    try {
      setMarkingPaid(true);

      const headers = buildHeaders(token);

      await axios.post(
        `${API_BASE}/invoice/invoices-mark-paid/${id}/`,
        { paid: true },
        { headers }
      );

      toast.success("Invoice marked as paid");

      onInvoiceUpdated?.({
        ...invoice,
        is_paid: true,
        payment_due: 0,
      });

      onClose?.();
    } catch (err) {
      console.error("❌ Failed to mark as paid:", err);
      const msg =
        err?.response?.data?.detail ||
        "Failed to mark invoice as paid. Check console.";
      toast.error(msg);
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleShare = () => {
    if (!pdf_file_link) {
      toast.error("PDF link is not available for this invoice");
      return;
    }

    if (typeof window !== "undefined") {
      window.open(pdf_file_link, "_blank");
    }
  };

  return (
    <AnimatePresence>
      {invoice && (
        <>
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white rounded-2xl shadow-lg w-[580px] max-h-[90vh] overflow-y-auto p-8"
            >
              <button
                onClick={onClose}
                className="absolute right-5 top-5 text-gray-500 hover:text-black"
              >
                ✕
              </button>

              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Invoice #{invoice_number || "—"}
              </h2>

              <div className="flex items-center gap-4 mb-6">
                <img
                  src={customerImageUrl}
                  alt={customerName}
                  className="w-[70px] h-[70px] rounded-full object-cover"
                />
                <div>
                  <p className="text-lg font-semibold text-gray-800">
                    {customerName}
                  </p>
                  <p className="text-sm text-gray-500">{customerEmail}</p>
                </div>
              </div>

              <div className="flex gap-4 mb-6">
                <div className="flex flex-col p-4 border border-gray-100 rounded-xl shadow-sm w-1/3">
                  <span className="text-sm text-gray-500">Invoice date</span>
                  <span className="text-lg text-gray-800">
                    {formatDate(invoice_date)}
                  </span>
                </div>
                <div className="flex flex-col p-4 border border-gray-100 rounded-xl shadow-sm w-1/3">
                  <span className="text-sm text-gray-500">Total amount</span>
                  <span className="text-lg text-gray-800">
                    {typeof total === "number"
                      ? `$${total.toFixed(2)}`
                      : "$0.00"}
                  </span>
                </div>
                <div className="flex flex-col p-4 border border-gray-100 rounded-xl shadow-sm w-1/3">
                  <span className="text-sm text-gray-500">Payment due</span>
                  <span className="text-lg text-gray-800">
                    {paymentDueText}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-4">
                <p className="text-sm text-gray-500 mb-2">Items & Services</p>
                {services.length === 0 ? (
                  <p className="text-sm text-gray-400">No items or services</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {services.map((s) => (
                      <div
                        key={s.id}
                        className="flex justify-between text-gray-800 text-[15px]"
                      >
                        <div>
                          <p>{s.name}</p>
                          <p className="text-sm text-gray-400">
                            1x {s.price} {s.currency || "USD"}
                          </p>
                        </div>
                        <p>
                          {s.price} {s.currency || "USD"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-3 space-y-2 mb-6 text-gray-800 text-[15px]">
                <div className="flex justify-between">
                  <span className="font-semibold">Subtotal</span>
                  <span>
                    {typeof subtotal === "number"
                      ? `$${subtotal.toFixed(2)}`
                      : "$0.00"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">
                    Tax{taxPercent ? ` (${taxPercent}%)` : ""}
                  </span>
                  <span>
                    {typeof taxAmt === "number"
                      ? `$${taxAmt.toFixed(2)}`
                      : "$0.00"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">
                    Discount{discountPercent ? ` (${discountPercent}%)` : ""}
                  </span>
                  <span>
                    {typeof discountAmt === "number"
                      ? `-$${discountAmt.toFixed(2)}`
                      : "$0.00"}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-1">
                  <span>Total amount:</span>
                  <span>
                    {typeof total === "number"
                      ? `$${total.toFixed(2)}`
                      : "$0.00"}
                  </span>
                </div>
              </div>

              {invoice.paid || invoice.is_paid ? (
                <div className="w-full bg-green-100 text-green-700 py-3 rounded-xl text-base font-medium text-center mb-4 border border-green-300 flex items-center justify-center gap-2">
                  Marked <IoCheckmark />
                </div>
              ) : (
                <button
                  onClick={handleMarkAsPaid}
                  disabled={markingPaid}
                  className="w-full bg-indigo-600 text-white py-3 rounded-xl text-base font-medium hover:bg-indigo-700 mb-4 disabled:opacity-60"
                >
                  {markingPaid ? "Marking..." : "Mark as paid"}
                </button>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleShare}
                  className="flex-1 border-2 border-gray-800 rounded-xl py-3 font-medium text-gray-800 flex justify-center items-center gap-2"
                >
                  Share
                </button>

                <button
                  onClick={() => setShowEdit(true)}
                  className="flex-1 border-2 border-gray-800 rounded-xl py-3 font-medium text-gray-800 flex justify-center items-center gap-2"
                >
                  Edit
                </button>
              </div>
            </motion.div>
          </motion.div>

          <AnimatePresence>
            {showEdit && (
              <EditInvoiceModal
                invoice={invoice}
                onClose={() => setShowEdit(false)}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}